// A worked stone quarry: a rock body with a recessed terraced pit cut into its
// top, a stack of freshly-dressed blocks on the rim, and a timber lifting
// derrick leaning over the pit. Ported 1:1 from age-of-mcp's quarry.ts: the
// shared foundation/scaffold come from the library, and the four build stages
// are transcribed from stage(s).
import * as THREE from "three";
import { defineModel, foundationSlab, scaffold, benchWork, EFFECT_LOOP, type Part } from "../../lib";

const ROCK = "#7d7a73"; // cut mountain stone
const ROCKD = "#5d5a54"; // darker terraced ledge
const PIT = "#2a2825"; // dark pit floor
const TIMBER = "#6e4d2c"; // lifting-derrick timber
const BLOCK = "#9a968c"; // freshly dressed blocks

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), { geometry: new THREE.BoxGeometry(0.7, 0.18, 0.7), color: ROCKD, position: [0, 0.23, 0] }, ...scaffold(0.82, 0.5)];
  if (s === 2) return [foundationSlab(0.84), { geometry: new THREE.BoxGeometry(0.74, 0.3, 0.74), color: ROCKD, position: [0, 0.29, 0] }, ...scaffold(0.82, 0.8)];

  const rim = 0.14 + 0.34;
  return [
    foundationSlab(0.84),
    // The rock body with a recessed pit cut into the top (darker ledge + floor).
    { geometry: new THREE.BoxGeometry(0.78, 0.34, 0.78), color: ROCK, position: [0, 0.14 + 0.17, 0] },
    { geometry: new THREE.BoxGeometry(0.5, 0.22, 0.5), color: ROCKD, position: [0, 0.14 + 0.28, 0] },
    { geometry: new THREE.BoxGeometry(0.34, 0.04, 0.34), color: PIT, position: [0, 0.14 + 0.4, 0] },
    // A stack of freshly-cut dressed blocks on the rim.
    { geometry: new THREE.BoxGeometry(0.18, 0.12, 0.18), color: BLOCK, position: [0.3, rim + 0.06, 0.3] },
    { geometry: new THREE.BoxGeometry(0.16, 0.1, 0.16), color: BLOCK, position: [0.3, rim + 0.17, 0.3] },
    { geometry: new THREE.BoxGeometry(0.16, 0.1, 0.16), color: BLOCK, position: [0.12, rim + 0.06, 0.32] },
    // A timber lifting derrick leaning over the pit; the rope and the block it
    // hoists are named so the "active" animation can wind them up and down (the
    // rope shortens about its top end as the block rises).
    { geometry: new THREE.BoxGeometry(0.05, 0.6, 0.05), color: TIMBER, position: [-0.3, rim + 0.18, -0.28], rotation: [0.3, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.05, 0.05, 0.5), color: TIMBER, position: [-0.2, rim + 0.42, -0.02], rotation: [0.3, 0, 0] },
    { geometry: new THREE.CylinderGeometry(0.012, 0.012, 0.3, 4), color: "#3a2c1c", position: [-0.1, rim + 0.3, 0.12], name: "hoist-rope" },
    { geometry: new THREE.BoxGeometry(0.11, 0.11, 0.11), color: BLOCK, position: [-0.1, rim + 0.1, 0.12], name: "hoist-load" },
  ];
}

const RIM = 0.14 + 0.34;

export const quarry = defineModel({
  id: "quarry",
  tiles: { w: 1, h: 1 },
  batchId: "standard",
  name: "Quarry",
  tags: ["building", "quarry", "low-poly", "age-of-mcp", "classic", "animated"],
  summary: "Low-poly quarry.",
  description: "A worked stone pit that extracts stone on a slow timer.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [
    {
      // The derrick hoisting a block out of the pit: the load rises 0.18 while
      // the rope winds up (scales about its top end) to stay taut, then lowers.
      id: "active",
      label: "Hoist working",
      duration: EFFECT_LOOP,
      variants: ["complete"],
      tracks: [
        {
          target: "hoist-load",
          pivot: [-0.1, RIM + 0.1, 0.12],
          position: [
            { t: 0, value: [0, 0, 0] },
            { t: EFFECT_LOOP / 2, value: [0, 0.18, 0], ease: "easeInOut" },
            { t: EFFECT_LOOP, value: [0, 0, 0], ease: "easeInOut" },
          ],
        },
        {
          target: "hoist-rope",
          pivot: [-0.1, RIM + 0.45, 0.12],
          scale: [
            { t: 0, value: [1, 1, 1] },
            { t: EFFECT_LOOP / 2, value: [1, 0.4, 1], ease: "easeInOut" },
            { t: EFFECT_LOOP, value: [1, 1, 1], ease: "easeInOut" },
          ],
        },
      ],
    },
  ],
  stations: [
    { id: "ledge", position: [0.05, 0.48, 0.28], facing: Math.PI, description: "The cutting ledge." },
  ],
  interactions: [
    { id: "cut", label: "Cutting stone", station: "ledge", variants: ["complete"], ...benchWork() },
  ],
});
